import React, { ChangeEvent, useCallback, useRef, useState } from 'react';

import { Icon } from '../Icon';
import { colors } from '../../styles/tokens';
import {
  ActionsWrapper,
  ContainerInput,
  ContainerMidia,
  Input,
  LoadingWrapper,
  Overlay,
  PlayButton,
  PlayInfo,
  RemoveButton,
  ReplaceLabel,
} from './styles';

import noMidia from '../../assets/no-midia.svg';

import { api } from '../../services/api';
import { useToast } from '../../hooks/toast';
import { useFeature } from '../../hooks/features';
import { Loading } from '../Loading';
import { IExerciseMedia } from '../../domains/Exercise';

interface InputFileExerciseProps {
  exercise_id: string;
  position: 0 | 1;
  media?: IExerciseMedia;
  onChange: (media: IExerciseMedia | undefined) => void;
}

const supportedFileTypes = [
  '.png',
  '.jpg',
  '.jpeg',
  '.mp4',
  '.avi',
  'quicktime',
];

// Um slot (0 ou 1) do máximo de 2 mídias por exercício -- cada instância
// faz upload/substitui/remove só o item nesse slot, sem afetar o outro.
// Diferente da versão antiga, não participa do unform (nunca alimentava
// de verdade o data do submit principal -- salva na hora, direto na
// API, como qualquer ação independente).
const InputFileExercise: React.FC<InputFileExerciseProps> = ({
  exercise_id,
  position,
  media,
  onChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { addToast } = useToast();
  const hasMediaUploadFeature = useFeature('exercise_media_upload');

  const [loading, setLoading] = useState(false);

  const handleChangeFile = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];

      if (!selectedFile) {
        return;
      }

      const [, type] = selectedFile.type.split('/');

      if (
        !supportedFileTypes.some(x =>
          x.toUpperCase().includes(type.toUpperCase()),
        )
      ) {
        addToast({
          type: 'error',
          title: `Arquivo ${type} não suportado`,
          description: `Os tipos de arquivos suportados são ${supportedFileTypes.join(
            ', ',
          )}`,
        });
        return;
      }

      setLoading(true);

      try {
        const data = new FormData();
        data.append('file', selectedFile);
        data.append('position', String(position));

        const response = await api.patch(
          `exercises/midia/${exercise_id}`,
          data,
          { headers: { identificator: 'training-midia' } },
        );

        const updated = (response.data.media as IExerciseMedia[]).find(
          item => item.position === position,
        );

        onChange(updated);

        addToast({ type: 'success', title: 'Upload realizado com sucesso.' });
      } catch (err: any) {
        if (err?.response?.status === 413) {
          addToast({
            type: 'error',
            title: 'Arquivo muito grande',
            description:
              'O arquivo enviado excede o limite permitido. Tente novamente com um arquivo menor.',
          });
          addToast({
            type: 'info',
            title: 'Permitidos arquivos de até 5mb.',
          });
        } else if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Você não tem permissão para executar essa ação.',
          });
        } else {
          addToast({
            type: 'error',
            title: 'Erro na atualização',
            description:
              'Ocorreu um erro na atualização. tente novamente mais tarde.',
          });
        }
      } finally {
        setLoading(false);

        if (inputRef.current) {
          inputRef.current.value = '';
        }
      }
    },
    [addToast, exercise_id, position, onChange],
  );

  const handleRemoveFile = useCallback(async () => {
    setLoading(true);

    try {
      await api.delete(`exercises/midia/${exercise_id}/${position}`);

      onChange(undefined);

      addToast({ type: 'success', title: 'Arquivo removido com sucesso.' });
    } catch (err: any) {
      if (err?.response?.status === 403) {
        addToast({
          type: 'error',
          title: 'Você não tem permissão para executar essa ação.',
        });
      } else {
        addToast({
          type: 'error',
          title: 'Erro na atualização',
          description:
            'Ocorreu um erro na atualização. tente novamente mais tarde.',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [addToast, exercise_id, position, onChange]);

  return (
    <ContainerInput>
      <ContainerMidia>
        <img src={media?.thumbnail_url || noMidia} alt="Mídia do exercício" />
      </ContainerMidia>

      <Overlay />

      {loading && (
        <LoadingWrapper>
          <Loading no_shadow />
        </LoadingWrapper>
      )}

      {media?.type === 'video' && !loading && (
        <>
          <PlayButton
            type="button"
            title="Assistir vídeo"
            onClick={() => window.open(media.url)}
          >
            <Icon name="play_arrow" size={28} color={colors.primary} />
          </PlayButton>
          <PlayInfo>
            <p>Guia visual</p>
            <p>Clique para assistir ao vídeo</p>
          </PlayInfo>
        </>
      )}

      <ActionsWrapper>
        {hasMediaUploadFeature ? (
          <label htmlFor={`file-${exercise_id}-${position}`}>
            <ReplaceLabel>
              <Icon name="cloud_upload" size={18} color={colors.onPrimary} />
              <span>{!media ? 'Enviar mídia' : 'Substituir mídia'}</span>
            </ReplaceLabel>
          </label>
        ) : (
          <ReplaceLabel
            locked
            title="Upload de mídia customizada é um recurso sob solicitação. Entre em contato para ativar."
          >
            <Icon
              name="lock"
              size={18}
              color={colors.onSurfaceVariant}
            />
            <span>Sob solicitação</span>
          </ReplaceLabel>
        )}

        {!!media && (
          <RemoveButton
            type="button"
            title="Remover mídia"
            onClick={handleRemoveFile}
          >
            <Icon name="delete_outline" size={18} color="currentColor" />
          </RemoveButton>
        )}
      </ActionsWrapper>

      <Input
        id={`file-${exercise_id}-${position}`}
        type="file"
        accept={supportedFileTypes.join(',')}
        ref={inputRef}
        onChange={handleChangeFile}
      />
    </ContainerInput>
  );
};

export { InputFileExercise };
